<template>
	<section
		:class="['Accordion', { 'isCollapsed': isCollapsed }]"
		aria-label="Toggle Accordion"
	>
		<div
			class="Header"
			@click="toggleCollapsed"
		>
			<span class="Title">{{ title }}</span>
			<div class="ToggleArea">
				<button
					v-if="showClear"
					class="Clear"
					aria-label="Clear Accordion"
					@click.stop.prevent="emit('clear')"
				>
					Clear
				</button>
				<i
					class="Toggle lnr-chevron-up"
					aria-hidden="true"
				></i>
			</div>
		</div>

		<div
			class="Body"
			:class="{ 'overflowVisible': isOverflowVisible }"
			@transitionend="onBodyTransitionEnd"
		>
			<div class="BodyContent">
				<slot name="content" />
			</div>
		</div>

		<div
			class="Footer"
			@click="toggleCollapsed"
		>
			<slot name="footer" />
		</div>
	</section>
</template>

<script setup lang="ts">
import { ref, type Ref } from 'vue';

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	showClear: {
		type: Boolean,
		default: true,
	},
	initiallyCollapsed: {
		type: Boolean,
		default: true,
	},
});

const emit = defineEmits<{
	(e: 'clear'): void;
	(e: 'collapseChange', isCollapsed: boolean): void;
}>();

const isCollapsed: Ref<boolean> = ref(props.initiallyCollapsed);
const isOverflowVisible: Ref<boolean> = ref(!props.initiallyCollapsed);

const toggleCollapsed = () => {
	const next = !isCollapsed.value;
	isCollapsed.value = next;

	if (next) {
		isOverflowVisible.value = false;
	}
	emit('collapseChange', next);
};

const onBodyTransitionEnd = (event: TransitionEvent) => {
	if (event.propertyName !== 'grid-template-rows') return;
	if (isCollapsed.value) return;

	isOverflowVisible.value = true;
};
</script>

<style scoped lang="scss">
	.Accordion {
		height: fit-content;
		background: $White;
		border-bottom: 1px solid $Grey-300;
		cursor: pointer;
		-webkit-tap-highlight-color: transparent;

		@include PaddingAreas(0, 0, 1.5, 0);

		@include Breakpoint(Small, Medium) {
			&.isCollapsed {
				border: none;
			}
		}

		@include Breakpoint(Large, ExtraLarge) {
			&:only-of-type {
				border-top: 1px solid $Grey-300;
				border-bottom: 1px solid $Grey-300;
			}
			&:first-child:not(:only-of-type) {
				border-top: none;
			}

			&:last-child:not(:only-of-type) {
				border-bottom: none;
			}
		}

		.Header {
			display: flex;
			justify-content: space-between;
			align-items: center;

			@include PaddingAreas(1.5, 1, 0, 1);

			.Title {
				color: $Grey-900;
				user-select: none;

				@include TextStyle(HeadingExtraSmall);
			}

			.ToggleArea {
				display: flex;
				align-items: center;
				gap: 1rem;
				user-select: none;

				.Clear {
					color: $Blue-700;
					cursor: pointer;

					@include TextStyle(BodySmallRegular);
				}

				.Toggle {
					min-width: 1rem;
					min-height: 1.375rem;
					font-size: 1rem;
					color: $Grey-900;
					transition: transform 0.3s ease;
					display: inline-block;
				}

				.Toggle, .Clear {
					background: none;
					border: none;
					padding: 0;
				}
			}
		}

		.Body {
			display: grid;
			grid-template-rows: 1fr;
			transition: grid-template-rows 0.24s ease-out;
			will-change: grid-template-rows;
			overflow: hidden;
			min-height: 0;

			.BodyContent {
				display: flex;
				overflow: hidden;
				min-height: 0;
				display: flex;
				justify-content: flex-start;
				align-items: stretch;
				transition: padding 0.24s ease-out;
				min-width: 0;

				@include PaddingAreas(1.5, 1, 0, 1);
			}
		}

		.Body.overflowVisible {
			overflow: visible;

			.BodyContent {
				overflow: visible;
			}
		}


		.Footer {
			display: none;
		}

		&.isCollapsed {
			@include PaddingAreas(0);

			.Header {
				@include PaddingAreas(1.5, 1);
			}

			.Toggle {
				transform: rotate(180deg);
			}

			.Body {
				grid-template-rows: 0fr;
				overflow: hidden;
			}

			.BodyContent {
				overflow: hidden;

				@include PaddingAreas(0, 1);
			}

			.Footer {
				display: block;

				margin-top: -1.5rem;
				@include PaddingAreas(0, 1, 1.5, 1);
			}
		}
	}
</style>
